import styled from '@emotion/styled';
import { useTranslation } from '@hotjar/translations';
import { Link, tokens, Icon, Loader } from '@hotjar/hotsauce';
import type {
  Categories,
  FeedbackData,
  HeatmapsData,
  InsightsData,
  RecordingsData,
  SurveyData,
} from '@hotjar/webapp/domains/activation/featured-insights/types';
import { FeedbackMainSection } from './MainSectionInsights/FeedbackMainSection';
import { SurveyMainSection } from './MainSectionInsights/SurveyMainSection';
import GENERIC_RECORDING_BLUR from './assets/generic_recording_blur.jpg';
import GENERIC_HEATMAP_BLUR from './assets/generic_heatmaps_blur.jpg';

const IconWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 50px;
  height: 50px;
  background-color: ${tokens.color.primary_cta};
  border-radius: 50%;
  transition: transform 150ms linear;
`;

const BigImageContainer = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  height: 100%;
  background-color: ${tokens.color.neutral_700};

  > svg {
    display: none;
  }

  :hover {
    & ${IconWrapper} {
      transform: translate(-50%, -50%) scale(1.2);
    }
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StyledPlayIcon = styled(Icon)`
  width: 24px;
  height: 24px;
  left: 2px;
  color: ${tokens.color.text_default_inverse};
`;

const StyledLoader = styled(Loader)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const RecordingMainSection = ({
  category,
  data,
  isLoading,
  onClick,
  thumbnail,
}: {
  category: Categories;
  data: RecordingsData;
  isLoading: boolean;
  onClick: (slideId: string) => void;
  thumbnail?: string;
}) => {
  const { t } = useTranslation();

  return (
    <BigImageContainer
      href={data?.url}
      title={t(`activation:space-overview.for-you.featured-insights.thumbnail-alt-recording`)}
      kind="standalone"
      onClick={() => onClick(category)}
      data-testid="slide-image-cta-recordings"
      isExternal
    >
      {
        (
          <>
            <Image src={isLoading || !thumbnail ? GENERIC_RECORDING_BLUR : thumbnail} />
            <IconWrapper>
              <StyledPlayIcon icon="play_alt" />
            </IconWrapper>
          </>
        ) as string & React.ReactNode
      }
    </BigImageContainer>
  );
};

const HeatmapMainSection = ({
  category,
  data,
  onClick,
  isLoading,
  thumbnail,
}: {
  category: Categories;
  data: HeatmapsData;
  isLoading: boolean;
  thumbnail?: string;
  onClick: (slideId: string) => void;
}) => {
  const { t } = useTranslation();

  return (
    <BigImageContainer
      href={data?.publicUrl}
      title={t('activation:space-overview.for-you.featured-insights.thumbnail-alt-heatmap')}
      kind="standalone"
      onClick={() => onClick(category)}
      data-testid="slide-image-cta-heatmaps"
    >
      {
        (
          <>
            {isLoading ? <StyledLoader size="l" /> : null}
            <Image src={isLoading || !thumbnail ? GENERIC_HEATMAP_BLUR : thumbnail} />
          </>
        ) as string & React.ReactNode
      }
    </BigImageContainer>
  );
};

export type FeaturedInsightsMainProps = {
  category: Categories;
  data: InsightsData;
  isLoading: boolean;
  onClick: (slideId: string) => void;
  thumbnail?: string;
};

export const FeaturedInsightMain = ({
  category,
  data,
  onClick,
  isLoading,
  thumbnail,
}: FeaturedInsightsMainProps): JSX.Element => {
  const renderMainSection = () => {
    switch (category) {
      case 'recordings':
        return (
          <RecordingMainSection
            isLoading={isLoading}
            thumbnail={thumbnail}
            data={data as RecordingsData}
            category={category}
            onClick={() => onClick(category)}
          />
        );
      case 'heatmaps':
        return (
          <HeatmapMainSection
            data={data as HeatmapsData}
            category={category}
            thumbnail={thumbnail}
            isLoading={isLoading}
            onClick={() => onClick(category)}
          />
        );
      case 'feedback':
        return (
          <FeedbackMainSection
            data={data as FeedbackData}
            category={category}
            onClick={() => onClick(category)}
          />
        );
      case 'surveys':
        return <SurveyMainSection data={data as SurveyData} />;

      default:
        return (
          <RecordingMainSection
            isLoading={isLoading}
            thumbnail={thumbnail}
            data={data as RecordingsData}
            category={category}
            onClick={() => onClick(category)}
          />
        );
    }
  };

  return renderMainSection();
};
